import { useEffect, useRef, useState, useSyncExternalStore } from "react";

import { dismissToast, TOAST_DURATION_MS, useToast, type Toast } from "../../lib/toast";
import { CloseIcon } from "../icons";

// A Drawer is a modal <dialog>: while one is open, the rest of the page is inert and sits under its backdrop, where a
// toast could be neither seen nor announced. A ConfirmDialog is a modal <dialog> too, and is meant to count the same
// way: a toast raised while one is open waits for it. Added and removed nodes are watched too, so a dialog that leaves
// the page while open (its page unmounted) stops counting.
function subscribeToDialogs(onChange: () => void): () => void {
	const observer = new MutationObserver(onChange);
	observer.observe(document.body, { subtree: true, childList: true, attributeFilter: ["open"] });
	return () => {
		observer.disconnect();
	};
}

function isDialogOpen(): boolean {
	return document.querySelector("dialog[open]") !== null;
}

/**
 * The app-wide toast bar that `showToast()` from src/lib/toast.ts fills: one message at a time, centered above the foot
 * of the screen in inverted colors, as on ravnar.io/demo. The root route renders it once, outside the app shell, so the
 * public pages have it too. The region stays in the page while it is empty, so screen readers announce each message.
 * A toast shown while a Drawer is open waits until the drawer has closed; one already showing when a drawer opens is
 * dropped, so it never comes back out of context.
 */
export function Toaster() {
	const toast = useToast();
	const isDrawerOpen = useSyncExternalStore(subscribeToDialogs, isDialogOpen);
	// The id of the last toast the bar has shown.
	const shownIdRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		if (toast === undefined) {
			return;
		}
		if (!isDrawerOpen) {
			shownIdRef.current = toast.id;
		} else if (shownIdRef.current === toast.id) {
			dismissToast(toast.id);
		}
	}, [toast, isDrawerOpen]);

	return (
		<section
			aria-label="Notifications"
			aria-live="polite"
			className="pointer-events-none fixed inset-x-0 bottom-7 z-50 flex justify-center px-5"
		>
			{/* A new key per toast restarts the timer and the slide-in animation. */}
			{toast === undefined || isDrawerOpen ? null : <ToastBar key={toast.id} toast={toast} />}
		</section>
	);
}

function ToastBar({ toast }: { readonly toast: Toast }) {
	// The timer waits while a mouse pointer is on the bar or focus is inside it, and starts again from the full duration
	// when they leave. A tap on a touch screen does not pause it: no pointer would ever leave.
	const [isHovered, setIsHovered] = useState(false);
	const [isFocused, setIsFocused] = useState(false);
	const isPaused = isHovered || isFocused;
	// Where focus was before it moved into the bar, so dismissing from the keyboard does not drop focus on <body>.
	const focusBeforeRef = useRef<HTMLElement | null>(null);

	useEffect(() => {
		if (isPaused) {
			return;
		}
		const timer = setTimeout(() => {
			dismissToast(toast.id);
		}, TOAST_DURATION_MS);
		return () => {
			clearTimeout(timer);
		};
	}, [toast.id, isPaused]);

	return (
		<div
			className="pointer-events-auto flex max-w-[480px] items-center gap-2.5 rounded-[11px] bg-ink py-3 pr-3.5 pl-4 text-ctl text-bg shadow-toast transition-[translate] duration-[280ms] ease-[cubic-bezier(.34,1.56,.64,1)] motion-reduce:transition-none starting:translate-y-[90px]"
			onPointerEnter={event => {
				if (event.pointerType === "mouse") {
					setIsHovered(true);
				}
			}}
			onPointerLeave={() => {
				setIsHovered(false);
			}}
			onFocus={event => {
				setIsFocused(true);
				if (event.relatedTarget instanceof HTMLElement && !event.currentTarget.contains(event.relatedTarget)) {
					focusBeforeRef.current = event.relatedTarget;
				}
			}}
			onBlur={() => {
				setIsFocused(false);
			}}
		>
			<p className="flex-1">{toast.message}</p>
			<button
				type="button"
				aria-label="Dismiss"
				onClick={() => {
					const previous = focusBeforeRef.current;
					dismissToast(toast.id);
					if (previous?.isConnected === true) {
						previous.focus();
					}
				}}
				className="flex h-5 w-5 shrink-0 press cursor-pointer items-center justify-center rounded-ctl border-0 bg-transparent text-bg/60 transition-colors duration-[120ms] hover:text-bg focus-visible:outline-bg"
			>
				<CloseIcon className="h-[11px] w-[11px] fill-none stroke-current stroke-[2]" strokeLinecap="round" />
			</button>
		</div>
	);
}
